import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import CandidateMatchCard from '../components/CandidateMatchCard';
import { COMPANY_ROUTES } from '../constants';

// TypeScript interfaces for easy backend integration
export interface Candidate {
    id: string;
    name: string;
    jobTitle: string;
    experience: string;
    location: string;
    country: string;
    countryCode: string;
    matchScore: number;
    avatar: string;
    workType?: string;
    salaryRange?: string;
}

// Mock data - REPLACE THIS SECTION WITH BACKEND API CALL
const MOCK_CANDIDATES: Candidate[] = [
    {
        id: '1',
        name: 'Sara El-Masry',
        jobTitle: 'Senior Product Designer',
        experience: '5+ years experience at SaaS companies',
        location: 'UAE',
        country: 'United Arab Emirates',
        countryCode: 'AE',
        matchScore: 91,
        avatar: 'https://images.unsplash.com/photo-1494790108755-2616b612b1c5?w=150&h=150&fit=crop&crop=face'
    },
    {
        id: '2',
        name: 'Andre Collins',
        jobTitle: 'UX/UI Designer',
        experience: 'Experience in B2B dashboard interfaces',
        location: 'Germany',
        country: 'Germany',
        countryCode: 'DE',
        matchScore: 87,
        avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face'
    },
    {
        id: '3',
        name: 'Aisha Khan',
        jobTitle: 'UX Researcher',
        experience: 'Full Time • $30K-$35K',
        location: 'India',
        country: 'India',
        countryCode: 'IN',
        matchScore: 77,
        avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop&crop=face'
    }
];

const CandidateMatches: React.FC = () => {
    const navigate = useNavigate();
    const [candidates, setCandidates] = useState<Candidate[]>([]);
    const [loading, setLoading] = useState(true);

    // BACKEND INTEGRATION POINT - Replace this useEffect with your API call
    useEffect(() => {
        const fetchCandidates = async () => {
            setLoading(true);
            try {
                // TODO: Replace with actual API call
                // const response = await fetch('/api/candidates/matches');
                // const data = await response.json();
                // setCandidates(data);
                
                // Mock API delay
                await new Promise(resolve => setTimeout(resolve, 1000));
                setCandidates(MOCK_CANDIDATES);
            } catch (error) {
                console.error('Failed to fetch candidates:', error);
                setCandidates(MOCK_CANDIDATES); // Fallback to mock data
            } finally {
                setLoading(false);
            }
        };

        fetchCandidates();
    }, []);

    const handleViewAllRecommendations = () => {
        navigate(COMPANY_ROUTES.ALL_RECOMMENDATIONS);
    };

    const handleContinueToDashboard = () => {
        navigate('/companies/dashboard');
    };

    if (loading) {
        return <LoadingState />;
    }

    return (
        <div className="min-h-screen bg-[#F8F9FB] relative overflow-hidden">
            {/* Background Gradient Layer */}
            <div className="absolute inset-0 overflow-hidden">
                <div className="absolute top-0 left-0 w-full h-full">
                    <div className="absolute top-1/4 left-1/4 w-[600px] h-[400px] bg-gradient-to-br from-blue-300 via-blue-200 to-blue-100 rounded-full mix-blend-multiply filter blur-3xl opacity-40 animate-pulse-slow"></div>
                    <div className="absolute top-1/3 right-1/4 w-[500px] h-[500px] bg-gradient-to-br from-yellow-200 via-orange-200 to-pink-200 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-pulse-slow" style={{animationDelay: '2s'}}></div>
                    <div className="absolute bottom-1/4 left-1/3 w-[400px] h-[400px] bg-gradient-to-br from-pink-200 via-purple-200 to-indigo-200 rounded-full mix-blend-multiply filter blur-3xl opacity-35 animate-pulse-slow" style={{animationDelay: '4s'}}></div>
                </div>
            </div>
            
            {/* Content Layer */}
            <div className="relative z-10 flex flex-col items-center min-h-screen px-4 py-12">
                <div className="max-w-6xl mx-auto text-center">
                    {/* Logo Section */}
                    <div className="mb-12 animate-fade-in-down">
                        <div className="flex justify-center items-center">
                            <img 
                                src="/branding/logo_blue.svg" 
                                alt="Hush" 
                                style={{
                                    width: '164px',
                                    height: '75px',
                                    objectFit: 'contain',
                                    filter: 'brightness(0) saturate(100%) invert(27%) sepia(95%) saturate(1574%) hue-rotate(212deg) brightness(91%) contrast(101%)'
                                }}
                            />
                        </div>
                    </div>

                    {/* Hero Section */}
                    <div className="mb-12">
                        <h1 
                            className="animate-fade-in-up"
                            style={{
                                fontFamily: 'Manrope',
                                fontWeight: 700,
                                fontSize: 'clamp(32px, 4vw, 48px)',
                                lineHeight: '110%',
                                letterSpacing: '-0.02em',
                                color: '#2563EB',
                                marginBottom: '24px',
                                animationDelay: '0.2s'
                            }}
                        >
                            Your first matches are ready!
                        </h1>

                        <p 
                            className="animate-fade-in-up"
                            style={{
                                fontFamily: 'Manrope',
                                fontWeight: 400,
                                fontSize: 'clamp(16px, 2vw, 20px)',
                                lineHeight: '150%',
                                color: '#6B7280',
                                maxWidth: '800px',
                                margin: '0 auto',
                                animationDelay: '0.4s'
                            }}
                        >
                            Our AI analyzed your job post and matched you with top candidates who align with your role requirements, skills, and preferences.
                        </p>
                    </div>

                    {/* Candidate Cards Grid */}
                    <div 
                        className="grid lg:grid-cols-3 gap-8 mb-12 animate-fade-in-up"
                        style={{ animationDelay: '0.6s' }}
                    >
                        {candidates.map((candidate, index) => (
                            <CandidateMatchCard 
                                key={candidate.id}
                                candidate={candidate}
                                animationDelay={0.8 + (index * 0.1)}
                            />
                        ))}
                    </div>

                    {/* Action Buttons */}
                    <div 
                        className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in-up"
                        style={{ animationDelay: '1.2s' }}
                    >
                        <button
                            onClick={handleViewAllRecommendations}
                            style={{
                                fontFamily: 'Manrope',
                                fontWeight: 600,
                                fontSize: '16px',
                                color: '#2563EB',
                                background: 'transparent',
                                border: '2px solid #2563EB',
                                borderRadius: '12px',
                                padding: '14px 32px',
                                cursor: 'pointer',
                                transition: 'all 0.3s ease',
                                minWidth: '200px'
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.background = '#2563EB';
                                e.currentTarget.style.color = '#FFFFFF';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.background = 'transparent';
                                e.currentTarget.style.color = '#2563EB';
                            }}
                        >
                            View All Recommendations
                        </button>

                        <button
                            onClick={handleContinueToDashboard}
                            style={{
                                fontFamily: 'Manrope',
                                fontWeight: 600,
                                fontSize: '16px',
                                color: '#FFFFFF',
                                background: '#2563EB',
                                border: 'none',
                                borderRadius: '12px',
                                padding: '16px 32px',
                                cursor: 'pointer',
                                boxShadow: '0px 8px 24px rgba(37, 99, 235, 0.4)',
                                transition: 'all 0.3s ease',
                                minWidth: '200px'
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.background = '#1D4ED8';
                                e.currentTarget.style.transform = 'translateY(-2px)';
                                e.currentTarget.style.boxShadow = '0px 12px 32px rgba(37, 99, 235, 0.5)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.background = '#2563EB';
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.boxShadow = '0px 8px 24px rgba(37, 99, 235, 0.4)';
                            }}
                        >
                            Continue to Dashboard
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

// Loading component
const LoadingState: React.FC = () => (
    <div className="min-h-screen bg-[#F8F9FB] flex items-center justify-center">
        <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p style={{ fontFamily: 'Manrope', color: '#6B7280' }}>Loading your matches...</p>
        </div>
    </div>
);

export default CandidateMatches;
